import React, { Component, useState } from 'react';
import './hamburger.scss';

import { joinClasses } from '../../../utils/helpers';

import chevDown from '../../../assets/images/chev-down-royal.svg';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
import { useUnifiedAnalytics } from '../../../contexts/AnalyticsWrapper';

/** @import { HamburgerProps, HamburgerLink, HamburgerLinkWithChild, HamburgerLinkNoChild, TrackingCallback } from './HamburgerTypes' */

/** @type {HamburgerLink[]} */
const LINKS_ORIGINAL = [
  { label: 'Dashboard', href: '/', closeMenu: true },
  { label: 'My profile', href: '/my-profile', closeMenu: true },
  { label: 'Manage account(s)', href: '/account-details' },
  { label: 'My documents', href: '/my-documents' },
  {
    label: 'Help', childLinks: [
      { label: "FAQs", href: window.raeEnv.productPageUrl + '/faqs', newTab: true, },
      { label: 'Contact us', href: '/contact-us' }
    ]
  },
  { label: 'Legal', href: '/legal' }
];

const followLinkWithTracking = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ e, /** @type {string} */ href, /** @type {undefined | TrackingCallback} */ trackingCallback) => {
  e.preventDefault();
  trackingCallback && trackingCallback(href);
  window.location.href = href;
};

const HamburgerLink = (/** @type {HamburgerLinkNoChild & { className: string; closeMenuFunc?: () => void; }} */ { label, href = '', newTab, className, closeMenu, trackingCallback = undefined }) => (
    <li className={className}>
      {
        closeMenu
            ? <a className="hamburger__anchor" onClick={e => followLinkWithTracking(e, href, trackingCallback)}>{label}</a>
            : <a className="hamburger__anchor" onClick={e => followLinkWithTracking(e, href, trackingCallback)} target={newTab ? "_blank" : undefined}>{label}</a>
      }
    </li>
);


const HamburgerWithChildren = (/** @type {HamburgerLinkWithChild} */ { label, childLinks }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
    event.preventDefault();
    setIsOpen(!isOpen);
  };
  let className = joinClasses(
      'hamburger__link-item',
      'hamburger__link-item--with-children',
      isOpen ? 'hamburger__link-item--open' : ''
  );
  let listClassName = joinClasses(
      'hamburger__list',
      !isOpen ? 'hamburger__list--closed' : ''
  );
  return (
      <li className={className}>
        <a className="hamburger__anchor" href="#" onClick={toggle}>
          <span>{label}</span>
          <img src={chevDown} />
        </a>
        <ul className={listClassName}>
          {childLinks.map(child => <HamburgerLink key={child.label} {...child} className="hamburger__link-item hamburger__link-item--child" />)}
        </ul>
      </li>

  );
}

/** @typedef {Object} AnalyticsProps
 * @property {Object} analytics - The analytics object
 * @property {function} analytics.trackAnalyticsClickEvent - Method to track click events
 */

/** @extends {Component<HamburgerProps & AnalyticsProps>} */
class Hamburger extends Component {
  /** @type {React.RefObject<HTMLDivElement>} */
  elementRef = React.createRef();

  componentDidMount() {
    document.body.addEventListener('click', this.onClickOutside, false);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.onClickOutside);
  }

  onClickOutside = (/** @type {MouseEvent} */ event) => {
    if (!this.elementRef.current) return;

    if (this.props.open &&
        // @ts-expect-error may have the types wrong
        !this.elementRef.current.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      if (this.props.onDismiss) this.props.onDismiss();
    }
  }

  renderLink(/** @type {HamburgerLink} */ link) {
    if (link.childLinks) {
      return <HamburgerWithChildren key={link.label} {...link} />;
    } else {
      return <HamburgerLink key={link.label} closeMenuFunc={this.props.onDismiss} {...link} className="hamburger__link-item" />;
    }
  }

  render() {
    const { open, analytics } = this.props;

    const LINKS = [
      { label: 'Dashboard', href: '/', closeMenu: true },
      { label: 'My profile', href: '/my-profile', closeMenu: true },
      { label: 'My documents', href: '/my-documents' },
      { label: 'My linked account', href: '/my-linked-account' },
      { label: 'Manage account(s)', href: '/account-details' },
      {
        label: 'Open another account',
        href: '/new-account',
        trackingCallback: (/** @type {string} */ url) => {
          if (!analytics) return;
          analytics.trackAnalyticsClickEvent({
            eventName: 'open a new account',
            eventUrl: `${window.location.origin}${url}`,
            eventLocation: 'burger menu',
            productName: 'notice-saver',
            journeyName: 'ns withdrawal journey',
            additionalProps: {
              productInfo: {
                productName: 'notice-saver',
                productID: 'ns withdrawal journey'
              }
            },
            eventType: 'link click',
            eventValue: 'burger menu|link click|open another account'
          });
        }
      },
      {
        label: 'Help', childLinks: [
          { label: "FAQs", href: window.raeEnv.productPageUrl + '/faqs', newTab: true, },
          { label: 'Contact us', href: '/contact-us' }
        ]
      },
      { label: 'Legal', href: '/legal' }
    ];


    return (
        <div ref={this.elementRef} className={joinClasses('hamburger', open ? 'hamburger--open' : '')}>
          <ul className="hamburger__list">
            {
              this.props.featureEnabled('EnableSecondaryAccounts')
                  ? LINKS.map(link => this.renderLink(link))
                  : LINKS_ORIGINAL.map(link => this.renderLink(link))
            }
          </ul>
        </div>
    );
  }
}


/**
 * @type {import('react').FC<HamburgerProps>}
 */
const HamburgerWithHooks = (props) => {
  const analytics = useUnifiedAnalytics();

  return <Hamburger {...props} analytics={analytics} />;
};

export default withFeatureFlags(HamburgerWithHooks);
